import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { AppSpecPage } from '@/components/apps/AppSpecPage';
import { AppSpecHistory } from '@/components/apps/AppSpecHistory';
import { type AppSpecProposal, proposeAppSpecChange, saveAppSpec } from '@/lib/app-spec';
import type { AppSpec } from '@shared/types/app-spec';

interface AppSpecEditorProps {
  appId: string;
  appKey: string;
  /** The description on screen (stored or default) and its version. */
  spec: AppSpec;
  version: number;
  /** Where « change the tool itself » goes (the Forge). */
  forgeHref: string | null;
  onApplied: () => void;
}

/**
 * F6c, in the Forge, for the app's creator and managers: ask a change of the app in words,
 * see the touched pages with the real data, then apply it or not. The tool,
 * its data and its access never change here.
 */
export const AppSpecEditor = ({ appId, appKey, spec, version, forgeHref, onApplied }: AppSpecEditorProps) => {
  const [request, setRequest] = useState('');
  const [pending, setPending] = useState<{ request: string; proposal: AppSpecProposal; baseVersion: number } | null>(null);
  const [busy, setBusy] = useState<'asking' | 'applying' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const ask = async () => {
    const asked = request.trim();
    if (!asked || busy) return;
    setBusy('asking'); setError(null); setNotice(null); setPending(null);
    try {
      const { proposal, baseVersion } = await proposeAppSpecChange(appId, asked);
      setPending({ request: asked, proposal, baseVersion });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'La modification n’a pas pu être préparée.');
    } finally {
      setBusy(null);
    }
  };

  const apply = async () => {
    if (!pending || pending.proposal.outcome !== 'patch' || busy) return;
    setBusy('applying'); setError(null);
    try {
      await saveAppSpec(appId, pending.baseVersion, pending.proposal.spec, 'chat', pending.request);
      setPending(null); setRequest(''); setNotice('Modification appliquée. Elle se défait depuis l’historique.');
      onApplied();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'La modification n’a pas pu être appliquée.');
    } finally {
      setBusy(null);
    }
  };

  const proposal = pending?.proposal;
  const touchedPages = proposal?.outcome === 'patch'
    ? proposal.spec.pages.filter((page) => !page.blocks.some((block) => block.type === 'runs')
      && (proposal.changedIds.includes(page.id) || page.blocks.some((block) => proposal.changedIds.includes(block.id))))
    : [];

  return (
    <section aria-label="Modifier l’application" className="space-y-3 rounded-2xl border border-domino-cardBorder bg-domino-warm p-4">
      <h2 className="text-sm font-semibold text-domino-ink">Modifier la mise en page</h2>
      <form className="flex flex-col gap-2 sm:flex-row" onSubmit={(event) => { event.preventDefault(); void ask(); }}>
        <label className="min-w-0 flex-1">
          <span className="sr-only">Demander un changement de l’application</span>
          <input value={request} onChange={(event) => setRequest(event.target.value)} disabled={busy !== null}
            placeholder="Demander un changement : « ajoute une page Équipe avec la moyenne par critère »…"
            className="w-full rounded-lg border border-domino-cardBorder bg-domino-white px-3 py-2 text-sm text-domino-ink placeholder:text-domino-inkSoft focus:border-domino-aubergine focus:outline-none" />
        </label>
        <Button type="submit" disabled={!request.trim() || busy !== null}>{busy === 'asking' ? 'Domino prépare…' : 'Proposer'}</Button>
      </form>
      {busy === 'asking' && <p role="status" className="text-sm text-domino-inkSoft">Domino réfléchit …</p>}
      {error && <p role="alert" className="text-sm text-[var(--domino-critical)]">{error}</p>}
      {notice && <p role="status" className="text-sm text-domino-ink">{notice}</p>}

      {proposal && (
        <div className="space-y-3 rounded-xl border border-domino-cardBorder bg-domino-white p-4">
          <p className="text-sm text-domino-ink">{proposal.message}</p>
          {proposal.outcome === 'patch' && (
            <>
              <p className="text-xs text-domino-inkSoft">Pages après la modification : {proposal.spec.pages.map((page) => page.label).join(' · ')}</p>
              {touchedPages.map((page) => (
                <div key={page.id} className="rounded-xl border border-dashed border-domino-cardBorder p-3">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-domino-inkSoft">Aperçu · {page.label}</p>
                  <AppSpecPage appId={appId} appKey={appKey} canManage={false} page={page} />
                </div>
              ))}
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => void apply()} disabled={busy !== null}>{busy === 'applying' ? 'Application…' : 'Appliquer'}</Button>
                <Button variant="secondary" onClick={() => setPending(null)} disabled={busy !== null}>Annuler</Button>
              </div>
            </>
          )}
          {proposal.outcome === 'needs_new_data' && (
            <p className="text-sm text-domino-inkSoft">
              Il manque une donnée{proposal.missing ? ` : ${proposal.missing}` : ''}. L’outil lui-même doit la relever.
              {forgeHref && <> <a className="text-domino-aubergine underline" href={forgeHref}>Modifier l’outil dans la Forge</a></>}
            </p>
          )}
          {proposal.outcome === 'not_possible' && proposal.missing && <p className="text-xs text-domino-inkSoft">Brique manquante : {proposal.missing}</p>}
          {proposal.outcome === 'invalid' && <p className="text-xs text-domino-inkSoft">{proposal.issues.join(' · ')}</p>}
        </div>
      )}
      <AppSpecHistory appId={appId} currentVersion={version} currentSpec={spec} onRestored={onApplied} />
    </section>
  );
};
